import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, Image, LogOut, MessageSquare } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const LOGO_URL = "/images/logo.jpeg";

const AdminLayout = () => {
  const { logout, admin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const navItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/admin/products", icon: Package, label: "Products" },
    { path: "/admin/homepage-media", icon: Image, label: "Homepage" },
    { path: "/admin/enquiries", icon: MessageSquare, label: "Enquiries" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-deep-black flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-charcoal min-h-screen flex-shrink-0 hidden md:flex flex-col border-r border-royal-gold/10">
        <div className="p-6 border-b border-royal-gold/10">
          <Link to="/admin" className="block">
            <img src={LOGO_URL} alt="Admin" className="h-12 mx-auto" />
          </Link>
        </div>
        
        <nav className="p-4 space-y-2 flex-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
              className={`flex items-center space-x-3 px-4 py-3 transition-colors ${
                isActive(item.path)
                  ? "bg-royal-gold/20 text-royal-gold border-l-2 border-royal-gold"
                  : "text-cream/60 hover:bg-velvet hover:text-cream"
              }`}
            >
              <item.icon size={18} />
              <span className="font-body text-sm">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="p-4 border-t border-royal-gold/10">
          <div className="text-cream/40 text-xs mb-3 px-4 truncate font-body">
            {admin?.email}
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="flex items-center space-x-3 px-4 py-3 w-full text-cream/60 hover:bg-velvet hover:text-cream transition-colors"
          >
            <LogOut size={18} />
            <span className="font-body text-sm">Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-charcoal z-50 px-4 py-3 flex items-center justify-between border-b border-royal-gold/10">
        <img src={LOGO_URL} alt="Admin" className="h-8" />
        <button onClick={handleLogout} className="text-cream/60 hover:text-cream">
          <LogOut size={20} />
        </button>
      </div>

      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-charcoal z-50 flex items-center justify-around py-3 border-t border-royal-gold/10">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex flex-col items-center p-2 ${
              isActive(item.path) ? "text-royal-gold" : "text-cream/50"
            }`}
          >
            <item.icon size={20} />
            <span className="text-xs mt-1 font-body">{item.label.split(" ")[0]}</span>
          </Link>
        ))}
      </div>

      {/* Main Content */}
      <main className="flex-1 mt-14 md:mt-0 mb-20 md:mb-0 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
